import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown"]
  static values  = { orgSlug: String }

  connect() {
    this.activeIndex = -1
    this._searchTimer = null
    this._mentionActive = false
    this._handleClickOutside = this._clickOutside.bind(this)
    document.addEventListener("click", this._handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
    if (this._searchTimer) clearTimeout(this._searchTimer)
  }

  onInput() {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart

    // Find the last @ before cursor that isn't already closed with ]
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")

    if (atIdx === -1 || before.slice(atIdx).includes("]")) {
      this._hide()
      return
    }

    const query = before.slice(atIdx + 1).trim()
    this._mentionActive = true

    if (query.length < 2) {
      this._showEmpty()
      return
    }

    this._search(query)
  }

  onKeydown(event) {
    if (!this._mentionActive) return

    const items = this.dropdownTarget.querySelectorAll("[data-mention-item]")

    if (event.key === "Escape") {
      event.preventDefault()
      this._hide()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1)
      this._highlightItem(items)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this._highlightItem(items)
      return
    }

    if (event.key === "Enter" && this.activeIndex >= 0 && items.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      const item = items[this.activeIndex]
      this._selectItem(item.dataset.display, item.dataset.bqName)
      return
    }
  }

  select(event) {
    const el = event.currentTarget
    this._selectItem(el.dataset.display, el.dataset.bqName)
  }

  // Private

  _search(query) {
    if (this._searchTimer) clearTimeout(this._searchTimer)

    this._searchTimer = setTimeout(async () => {
      try {
        const resp = await fetch(`/${this.orgSlugValue}/entities/search?q=${encodeURIComponent(query)}`)
        const results = await resp.json()
        this._renderResults(results)
      } catch (e) {
        this._hide()
      }
    }, 300)
  }

  _renderResults(results) {
    this.activeIndex = -1
    const dd = this.dropdownTarget

    if (results.length === 0) {
      dd.innerHTML = `<div class="px-4 py-3 text-sm text-gray-400">No matching records</div>`
      dd.classList.remove("hidden")
      return
    }

    // Group by table_name
    const grouped = {}
    results.forEach(r => {
      if (!grouped[r.table_name]) grouped[r.table_name] = []
      grouped[r.table_name].push(r)
    })

    let html = ""
    for (const [tableName, items] of Object.entries(grouped)) {
      html += `<div class="px-4 py-1.5 text-[11px] font-medium text-gray-400 uppercase tracking-wider">${this._escapeHtml(tableName)}</div>`
      items.forEach(item => {
        html += `<div data-mention-item data-display="${this._escapeAttr(item.display)}" data-bq-name="${this._escapeAttr(item.bq_name)}"
                      data-action="click->chat-mention#select"
                      class="flex items-center gap-3 px-4 py-2 mx-2 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors">
          <div class="w-6 h-6 flex items-center justify-center bg-emerald-50 rounded-md flex-shrink-0">
            <span class="text-emerald-600"><i data-lucide="database" class="w-3 h-3 inline"></i></span>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm text-gray-700 truncate">${this._escapeHtml(item.display)}</div>
          </div>
        </div>`
      })
    }

    dd.innerHTML = html
    dd.classList.remove("hidden")
  }

  _showEmpty() {
    const dd = this.dropdownTarget
    dd.innerHTML = `<div class="px-4 py-3 text-sm text-gray-400">Type to search records...</div>`
    dd.classList.remove("hidden")
  }

  _hide() {
    this._mentionActive = false
    this.activeIndex = -1
    if (this._searchTimer) clearTimeout(this._searchTimer)
    this.dropdownTarget.classList.add("hidden")
  }

  _selectItem(display, bqName) {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")

    if (atIdx === -1) return

    const after = val.slice(pos)
    const mention = `@${display} [${bqName}]`
    input.value = val.slice(0, atIdx) + mention + " " + after
    input.focus()

    const newPos = atIdx + mention.length + 1
    input.setSelectionRange(newPos, newPos)

    this._hide()
  }

  _highlightItem(items) {
    items.forEach((item, i) => {
      item.classList.toggle("bg-purple-50", i === this.activeIndex)
    })
  }

  _clickOutside(event) {
    if (!this._mentionActive) return
    if (this.element.contains(event.target)) return
    this._hide()
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    return str.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
